/**
 * Merge rules for sync — pure functions, no IndexedDB, no network.
 *
 * Kept separate from sync.js precisely so they can be tested without a browser:
 * this is where the subtle bugs live, not in the plumbing.
 *
 * Conflicts are rare by construction — one user owns the data (ownerUserId), so
 * concurrent edits mean the same person on two devices. Last-write-wins on
 * `updatedAt` is therefore sufficient, and matches the prototype's own
 * single-writer model. Media is immutable once captured and never conflicts.
 */

/**
 * Pick the winner between a local and a remote version of one record.
 *
 * Rules, in order:
 *  1. Only one side has it → that side wins.
 *  2. A tombstone wins over an edit at the same or older timestamp. A delete is
 *     an intentional act; resurrecting a supplier the buyer deleted is worse
 *     than losing a later incidental edit.
 *  3. Otherwise the newer `updatedAt` wins; ties go to local, so a record the
 *     buyer just touched on this phone isn't overwritten by an identical echo.
 */
export function resolve(local, remote) {
  if (!local) return remote;
  if (!remote) return local;

  const lt = local.updatedAt || 0;
  const rt = remote.updatedAt || 0;

  if (local.deletedAt && !remote.deletedAt) return lt >= rt ? local : remote;
  if (remote.deletedAt && !local.deletedAt) return rt >= lt ? remote : local;

  return rt > lt ? remote : local;
}

/**
 * Merge a pulled list into the local list.
 *
 * Returns { merged, changed } where `changed` holds only the records whose
 * winning version differs from what was stored locally — so the caller writes
 * the minimum to IndexedDB instead of rewriting everything on every sync.
 */
export function mergeList(localList, remoteList, normalize = (x) => x) {
  const byId = new Map();
  (localList || []).forEach((r) => byId.set(r.id, r));

  const changed = [];
  (remoteList || []).forEach((raw) => {
    const remote = normalize(raw);
    if (!remote || !remote.id) return;
    const local = byId.get(remote.id);
    const winner = resolve(local, remote);
    if (winner !== local) {
      byId.set(remote.id, winner);
      changed.push(winner);
    }
  });

  return { merged: Array.from(byId.values()), changed };
}

/**
 * Drop tombstones from what the UI sees. They stay in IndexedDB — a delete made
 * offline has to survive the next pull, which it can only do by being a record
 * the server is told about rather than an absence.
 */
export const visible = (list) => (list || []).filter((r) => !r.deletedAt);

/**
 * Tombstones old enough that the server has certainly seen them can be dropped
 * for good. Conservative by design: a phone can be offline for the length of a
 * trip, so nothing is purged until well past that.
 */
export const PURGE_TOMBSTONES_AFTER_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

export function purgeableTombstones(list, now = Date.now()) {
  return (list || [])
    .filter((r) => r.deletedAt && now - r.deletedAt > PURGE_TOMBSTONES_AFTER_MS)
    .map((r) => r.id);
}
